import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import {
  answerBurial,
  burialCountryAwaitsConfirmation,
  chosenBurialCountry,
  presentationCountryCode,
  type PresentationOwnerProfile,
  type PresentationResolvedInput,
  type PresentationResolvedSection,
  type PresentationSectionAnswer,
} from '../../application';
import { JourneyDocuments } from './journey-documents';
import { JourneyStore } from './journey.store';
import { PublicJourneyConfig, PublicJourneyUseCases } from './public-journey.dependencies';

/** Buried where the owner lives, or in a country they name. */
export type BurialChoice = 'residence' | 'other';

/** The burial country a funeral answer carries, if it carries one. */
export const savedBurialCountry = (
  answers: readonly {
    readonly sectionId: { readonly value: string };
    readonly answerShape: string;
    readonly value: unknown;
  }[],
): string | null => {
  for (const answer of answers) {
    if (
      answer.sectionId.value === 'origin-burial' &&
      answer.answerShape === 'single' &&
      typeof answer.value === 'object' &&
      answer.value !== null &&
      'value' in answer.value &&
      typeof answer.value.value === 'string'
    ) {
      return answer.value.value;
    }
  }
  return null;
};

/**
 * Where the owner wishes to be buried: where they live, or a country they name. Public — it is
 * printed on the emergency card, for whoever finds it. The profile holds the choice; the funeral
 * answer carries it into the testament.
 *
 * A draft from before the country was asked for says 'origin', with a country the app set
 * (Guinea): the owner is asked once whether it is theirs, here, and nothing changes until they say.
 */
@Injectable()
export class FuneralWish {
  private readonly store = inject(JourneyStore);
  private readonly documents = inject(JourneyDocuments);
  private readonly useCases = inject(PublicJourneyUseCases);
  private readonly config = inject(PublicJourneyConfig);
  private readonly i18n = inject(TranslocoService);

  readonly section = signal<PresentationResolvedSection | null>(null);
  readonly input = signal<PresentationResolvedInput | null>(null);
  readonly choice = signal<BurialChoice | ''>('');
  /** The country picked from the full list, when the choice is another country. */
  readonly pick = signal('');

  /** The country an older draft holds without its owner having chosen it, or null. */
  unconfirmedCountry(): string | null {
    const profile = this.store.profile();
    if (!burialCountryAwaitsConfirmation(profile)) return null;
    return (
      savedBurialCountry(this.store.draft()?.publicAnswers ?? []) ??
      profile.originCountry?.value ??
      null
    );
  }

  async open(): Promise<void> {
    this.store.busy.set(true);
    this.store.errorMessage.set('');
    try {
      const profile = this.store.profile();
      const sections = await this.useCases.resolveSections.execute({ profile });
      const section =
        sections.find(
          (candidate) =>
            candidate.section.id.value === 'origin-burial' &&
            candidate.section.compartment === 'public',
        ) ?? null;
      this.section.set(section);
      if (section === null) throw new Error(this.i18n.translate('errors.funeralUnavailable'));
      const input = this.useCases.resolveQuestions.execute({ section, profile });
      this.input.set(input);
      if (input.answerShape !== 'single') {
        throw new TypeError('The funeral section must use the single answer shape');
      }
      this.preselect(profile);
      this.store.go('funeral');
    } catch (error: unknown) {
      this.store.fail(error, this.i18n.translate('errors.unexpected'));
    } finally {
      this.store.busy.set(false);
    }
  }

  get saveDisabled(): boolean {
    return (
      this.store.busy() ||
      this.choice() === '' ||
      (this.choice() === 'other' && this.pick().length === 0)
    );
  }

  async save(): Promise<void> {
    const section = this.section();
    if (this.saveDisabled || section === null) return;
    this.store.busy.set(true);
    this.store.errorMessage.set('');
    const profile = this.store.profile();
    const country =
      this.choice() === 'residence'
        ? profile.residenceCountry
        : presentationCountryCode(this.pick());
    const answer: PresentationSectionAnswer = Object.freeze({
      answerShape: 'single',
      section,
      value: country,
    });
    try {
      // The owner's answer, said here: the profile takes it, and an older draft's country that
      // was never chosen is not read again.
      const owner = answerBurial(profile, country);
      this.store.draft.set(
        await this.useCases.updateProfile.execute({ draftId: this.config.draftId, owner }),
      );
      this.store.profile.set(owner);
      this.store.draft.set(
        await this.useCases.saveSectionAnswers.execute({
          draftId: this.config.draftId,
          compartment: 'public',
          answer,
        }),
      );
      await this.documents.assemble();
      // The wish is saved: the card takes the funeral screen's place in the history, so any way
      // back — the arrow, the phone's, the browser's — lands on the wishes, not on the question.
      this.store.emergencyReturnView.set('dashboard');
      this.store.go('emergency', { replace: true });
    } catch (error: unknown) {
      this.store.fail(error, this.i18n.translate('errors.unexpected'));
    } finally {
      this.store.busy.set(false);
    }
  }

  /**
   * What the question opens on: the answer saved, else the profile's, else — for an older
   * draft — the country it holds, shown to be confirmed rather than taken as said.
   */
  private preselect(profile: PresentationOwnerProfile): void {
    const country =
      savedBurialCountry(this.store.draft()?.publicAnswers ?? []) ??
      chosenBurialCountry(profile)?.value ??
      this.unconfirmedCountry();
    if (country === null) {
      this.choice.set('');
      this.pick.set('');
    } else if (country === profile.residenceCountry.value) {
      this.choice.set('residence');
      this.pick.set('');
    } else {
      this.choice.set('other');
      this.pick.set(country);
    }
  }
}
